import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Row, Col, Button, Form, Spinner, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';

import PickedShipment from 'components/add_order/picklist/ProcessPicklist/PickedShipment';
import PicklistDetailSummary from 'components/add_order/picklist/PicklistDetail/PicklistDetailSummary';
import PicklistShipmentsPanel from 'components/add_order/picklist/ProcessPicklist/PicklistShipmentsPanel';
import PicklistProductsPanel from 'components/add_order/picklist/ProcessPicklist/PicklistProductsPanel';
import WebcamPreview from 'components/add_order/picklist/ProcessPicklist/WebcamPreview';
import PrintSizeDropdown from 'components/common/PrintSizeDropdown';

import { picklistApi, shipmentApi } from 'api';
import paths from 'routes/paths';
import {
  convertPdfToHtml,
  showAwbPdf,
  generateBlueDartAwbPdf,
  generateInvoicePdf,
} from 'utils/pdfGenerators';
import { printPicklistPdf } from 'utils/picklistPrint';
import { PRINT_PAGE_SIZE_STORAGE_KEYS } from 'constants/printPageSize';

import { useUserContext } from 'providers/UserProvider';
import useVideoRecording from 'hooks/useVideoRecording';

const ProcessPicklist = () => {
  const { selectedTenant, selectedWarehouse } = useUserContext();
  const navigate = useNavigate();
  const { picklistId } = useParams();
  const [searchParams] = useSearchParams();
  const picklistCode = searchParams.get('picklist_code');

  const inputRef = useRef(null);
  const prevPickedShipmentRef = useRef(null);
  const {
    stream: webcamStream,
    onShipmentStart,
    onShipmentEnd,
    onShipmentSidelined,
  } = useVideoRecording();

  const [pickedShipment, setPickedShipment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [livePickingRemaining, setLivePickingRemaining] = useState(0);

  const [showShipments, setShowShipments] = useState(false);
  const [showItems, setShowItems] = useState(false);

  const [inputType, setInputType] = useState('barcode'); // 'barcode' | 'shipment_code'
  const [inputValue, setInputValue] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);

  const notifyPicklistUpdated = useCallback(() => {
    window.dispatchEvent(new CustomEvent('picklist-updated'));
  }, []);

  const fetchPickedShipment = useCallback(async () => {
    try {
      setLoading(true);
      const response = await picklistApi.getPickedShipment(picklistId);
      setPickedShipment(response);
    } catch (err) {
      setPickedShipment(null);
    } finally {
      setLoading(false);
    }
  }, [picklistId]);

  const fetchShipmentOfPicklist = async (picklistId, shipmentId) => {
    try {
      const response = await picklistApi.getShipmentDetailOfPicklist(
        picklistId,
        shipmentId,
      );
      setPickedShipment(response);
    } catch (err) {
      setPickedShipment(null);
    }
  };

  useEffect(() => {
    if (!loading && !submitLoading) {
      inputRef.current?.focus();
    }
  }, [loading, submitLoading]);

  useEffect(() => {
    fetchPickedShipment();
  }, [picklistId, fetchPickedShipment]);

  // Batch-code picklists require live picking to be completed before packing.
  const checkLivePickingGate = useCallback(async () => {
    try {
      const response = await picklistApi.getLivePickingState(picklistId);
      setLivePickingRemaining(response?.progress?.remaining || 0);
    } catch (err) {
      // barcode-mode picklist (NOT_BATCH_CODE_PICKLIST) or older backend — no gate
      setLivePickingRemaining(0);
    }
  }, [picklistId]);

  useEffect(() => {
    checkLivePickingGate();
  }, [checkLivePickingGate]);

  useEffect(() => {
    if (!pickedShipment) {
      setInputType('barcode');
      return;
    }

    if (pickedShipment?.shipment_status === 'picked') {
      setInputType('barcode');
    }
  }, [pickedShipment]);

  useEffect(() => {
    const prev = prevPickedShipmentRef.current;
    if (
      prev &&
      prev.shipment_status === 'picked' &&
      !pickedShipment &&
      !loading
    ) {
      onShipmentSidelined();
    }
    prevPickedShipmentRef.current = pickedShipment;
  }, [pickedShipment, loading, onShipmentSidelined]);

  const isShowSelectBox = () => {
    if (!pickedShipment) return true;

    return pickedShipment?.shipment_status !== 'picked';
  };

  const updateLocalShipmentState = (
    shipmentData,
    shipmentLineId,
    shipmentLineStatus,
    shipmentLineStatusLabel,
    newShipmentStatus,
    newShipmentStatusLabel,
  ) => {
    return {
      ...shipmentData,
      shipment_status: newShipmentStatus,
      shipment_status_label: newShipmentStatusLabel,
      lines: shipmentData.lines.map(line => {
        if (line.id === shipmentLineId) {
          return {
            ...line,
            shipment_line_status: shipmentLineStatus,
            shipment_line_status_label: shipmentLineStatusLabel,
          };
        }
        return line;
      }),
    };
  };

  const handleClosePicklist = async picklistId => {
    try {
      await picklistApi.picklistStatusToClosed(picklistId);
      toast.success('Picklist closed successfully!');
      const tenant = selectedTenant?.domains?.[0]?.domain;
      const warehouse = selectedWarehouse?.domain;
      navigate(paths.picklists.list(tenant, warehouse));
    } catch (err) {
      toast.error('Fail to close picklist');
    }
  };

  const handlePrintPicklistPdf = async (picklistId, picklistCode, pageSize) => {
    try {
      const printContext = {
        tenantName: selectedTenant?.name,
        warehouseName: selectedWarehouse?.name,
      };
      await printPicklistPdf(
        picklistId,
        picklistCode,
        null,
        pageSize,
        printContext,
      );
    } catch (error) {
      toast.error('Error generating PDF');
    }
  };

  const handlePrintInvoiceAndAWBPdf = async shipmentId => {
    try {
      const response = await shipmentApi.getInvoiceDetail(shipmentId);

      const shipmentAWBFile = response?.shipment?.awb_file;
      const isBlueDart =
        response?.shipment?.shipping_provider?.shipping_provider_type ===
        'BLUEDART';

      if (isBlueDart) {
        generateBlueDartAwbPdf(response);
      } else if (shipmentAWBFile) {
        const awbHtml = await convertPdfToHtml(shipmentAWBFile);
        showAwbPdf(awbHtml, response);
      } else {
        generateInvoicePdf(response);
      }
    } catch (error) {
      toast.error('Error generating PDF');
    }
  };

  const handleInputSubmit = async e => {
    e.preventDefault();
    if (!inputValue) return;

    try {
      setSubmitLoading(true);

      if (inputType === 'shipment_code') {
        const response = await picklistApi.setShipmentStatusToPicked(
          picklistId,
          inputValue,
        );

        if (response?.skipped_lines?.length) {
          const skippedSkus = response.skipped_lines
            .map(line => line.product?.sku)
            .filter(Boolean)
            .join(', ');
          toast.warning(
            `${response.skipped_lines.length} item(s) of this shipment were not found during batch picking — reject them as "Not Found": ${skippedSkus}`,
            { autoClose: false },
          );
        }

        switch (response.shipment_status) {
          case 'picked':
            await fetchPickedShipment();
            onShipmentStart(response.id);
            break;

          case 'ready_to_ship':
            onShipmentStart(response.id);
            onShipmentEnd(response.id);
            await fetchShipmentOfPicklist(picklistId, response.id);
            notifyPicklistUpdated();
            await handlePrintInvoiceAndAWBPdf(response.id);
            break;

          case 'packed':
            onShipmentStart(response.id);
            onShipmentEnd(response.id);
            await fetchShipmentOfPicklist(picklistId, response.id);
            notifyPicklistUpdated();
            break;
        }
      } else if (inputType === 'barcode') {
        const response = await picklistApi.setShipmentLineStatusToPicked(
          picklistId,
          inputValue,
        );

        const shipmentStatus = response?.shipment.shipment_status;
        const shipmentId = response?.shipment?.id;

        const isNewShipment =
          !pickedShipment ||
          pickedShipment?.shipment_status === 'packed' ||
          pickedShipment?.shipment_status === 'ready_to_ship';

        if (isNewShipment) {
          onShipmentStart(shipmentId);
        }

        if (isNewShipment) {
          await fetchShipmentOfPicklist(picklistId, shipmentId);
        } else {
          setPickedShipment(prevShipment =>
            updateLocalShipmentState(
              prevShipment,
              response?.shipment_line?.id,
              response?.shipment_line?.shipment_line_status,
              response?.shipment_line?.shipment_line_status_label,
              response?.shipment.shipment_status,
              response?.shipment.shipment_status_label,
            ),
          );
        }

        if (shipmentStatus === 'ready_to_ship' || shipmentStatus === 'packed') {
          onShipmentEnd(shipmentId);
          notifyPicklistUpdated();
        }

        if (shipmentStatus === 'ready_to_ship') {
          await handlePrintInvoiceAndAWBPdf(shipmentId);
        }

        if (
          shipmentStatus === 'packed' &&
          !selectedWarehouse?.is_awb_auto_generate
        ) {
          await handlePrintInvoiceAndAWBPdf(shipmentId);
        }
      }
    } catch (err) {
      const data = err?.response?.data;
      const errorCode = [].concat(data?.error_code || [])[0];

      if (errorCode === 'LIVE_PICKING_INCOMPLETE') {
        toast.warning(
          'Batch picking is not completed yet. Finish it on the mobile page first.',
        );
        checkLivePickingGate();
      } else {
        toast.error('Fail to scan');
      }
    } finally {
      setSubmitLoading(false);
      setInputValue('');
    }
  };

  return (
    <div className="d-flex flex-column">
      <div className="mt-1 mb-3 d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Picklist #{picklistCode || picklistId}</h5>
        <div className="d-flex gap-2">
          <Button
            variant="falcon-default"
            size="sm"
            onClick={() => setShowShipments(true)}
          >
            View Shipments
          </Button>
          <Button
            variant="falcon-default"
            size="sm"
            onClick={() => setShowItems(true)}
          >
            View Items
          </Button>
          <Button
            variant="falcon-default"
            size="sm"
            onClick={() => handleClosePicklist(picklistId)}
          >
            Close Picklist
          </Button>
          <PrintSizeDropdown
            storageKey={PRINT_PAGE_SIZE_STORAGE_KEYS.PICKLIST}
            title="Print picklist"
            onPrint={pageSize =>
              handlePrintPicklistPdf(picklistId, picklistCode, pageSize)
            }
          />
        </div>
      </div>

      {livePickingRemaining > 0 && (
        <Alert variant="warning" className="py-2">
          <b>Batch picking is not completed</b> — {livePickingRemaining} unit(s)
          still need to be picked on the mobile batch picking page before
          packing can start. Open the picklist detail page to show the QR code.
        </Alert>
      )}

      <Row className="flex-grow-1">
        <Col md={9} className="mb-3 mb-md-0">
          {loading ? (
            <div className="d-flex justify-content-center align-items-center p-4">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : pickedShipment ? (
            <PickedShipment
              picklistId={picklistId}
              shipmentData={pickedShipment}
              onRefresh={fetchPickedShipment}
              onPicklistUpdate={notifyPicklistUpdated}
            />
          ) : (
            <div className="d-flex flex-column align-items-center justify-content-center h-100 py-5">
              <h2 className="text-700 mb-4">Start Packing</h2>
              <p className="text-500">
                Scan Barcode or Select Shipment to pack
              </p>
            </div>
          )}
        </Col>
        <Col md={3}>
          <PicklistDetailSummary
            picklistId={picklistId}
            showActionBtn={false}
            listenToUpdates={true}
          />
        </Col>
      </Row>

      <div className="fixed-bottom p-3 bg-white border-top">
        <Form onSubmit={handleInputSubmit} autoComplete="off">
          <Row className="align-items-center w-50">
            {isShowSelectBox() && (
              <Col xs="auto">
                <Form.Select
                  value={inputType}
                  onChange={e => setInputType(e.target.value)}
                  disabled={submitLoading || loading}
                >
                  <option value="barcode">Scan Barcode</option>
                  <option value="shipment_code">Scan Shipment Code</option>
                </Form.Select>
              </Col>
            )}
            <Col>
              <Form.Control
                ref={inputRef}
                placeholder={
                  inputType === 'shipment_code'
                    ? 'Scan Shipment Code'
                    : 'Scan Barcode'
                }
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                disabled={submitLoading || loading}
                autoComplete="off"
              />
            </Col>
          </Row>
        </Form>
      </div>

      <PicklistShipmentsPanel
        show={showShipments}
        onHide={() => setShowShipments(false)}
        picklistId={picklistId}
      />

      <PicklistProductsPanel
        show={showItems}
        onHide={() => setShowItems(false)}
        picklistId={picklistId}
      />

      <WebcamPreview stream={webcamStream} />
    </div>
  );
};

export default ProcessPicklist;
